/**
 * PWA Kit SDK adapter — modern implementation (React 19 / Vite brands).
 *
 * Vendored from `@salesforce/pwa-kit-react-sdk` 3.16, which pins React 18 and
 * react-router-dom 5. Same API as `./index.ts`.
 */
import React, {createContext, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {bundleBasePath, getEnvBasePath} from '@salesforce/pwa-kit-runtime/utils/ssr-namespace-paths';

/**
 * Holds the Express `req`/`res` during SSR (provided by `entry.server`). Empty on the client.
 */
export const ServerContext = createContext<{req?: unknown; res?: unknown}>({});

export const CorrelationIdContext = createContext<{correlationId?: string}>({});

type CorrelationIdProviderProps = {
	children: React.ReactNode;
	correlationId: string | (() => string);
	resetOnPageChange?: boolean;
};

export const CorrelationIdProvider = ({children, correlationId, resetOnPageChange = true}: CorrelationIdProviderProps) => {
	const [id, setId] = useState(() => (typeof correlationId === 'function' ? correlationId() : correlationId));
	const location = useLocation();
	const isFirstRun = useRef(true);

	useEffect(() => {
		if (isFirstRun.current) {
			isFirstRun.current = false;

			return;
		}

		if (resetOnPageChange && typeof correlationId === 'function') {
			setId(correlationId());
		}
	}, [location.pathname]);

	return <CorrelationIdContext.Provider value={{correlationId: id}}>{children}</CorrelationIdContext.Provider>;
};

/**
 * Server-side base URL of the bundle on Managed Runtime: `/mobify/bundle/<BUNDLE_ID>/`.
 * Vite client assets live under its `client/` folder.
 */
export const getBundleUrl = () => `${getEnvBasePath()}${bundleBasePath}/${process.env.BUNDLE_ID || 'development'}/`;

/**
 * Error carrying an HTTP status, rendered by the brand error page.
 */
export class HTTPError extends Error {
	status: number;

	/**
	 * @param status - HTTP status code
	 * @param message - error message
	 */
	constructor(status: number, message: string) {
		super(message);
		Object.setPrototypeOf(this, new.target.prototype);
		this.message = message;
		this.status = status;
	}

	/**
	 * @returns a readable description
	 */
	toString() {
		return `HTTPError ${this.status}: ${this.message}`;
	}
}

/**
 * 404 error.
 */
export class HTTPNotFound extends HTTPError {
	/**
	 * @param message - error message
	 */
	constructor(message: string) {
		super(404, message);
	}
}

/**
 * Returns the public URL of a file shipped in the bundle (e.g. `static/...`).
 * On the client the base comes from `window.Progressive.buildOrigin`, set by `entry.server`.
 */
export const getAssetUrl = (path?: string) => {
	// Assets imported in JS (fonts, images) already carry a full URL with Vite.
	if (path && /^(\/|[a-z]+:)/i.test(path)) {
		return path;
	}

	const publicPath =
		typeof window !== 'undefined'
			? `${window.Progressive.buildOrigin}`
			: getBundleUrl();

	return path ? `${publicPath}${path}` : publicPath;
};

export const getAppOrigin = () => {
	if (typeof window !== 'undefined') {
		return window.location.origin;
	}

	const {APP_ORIGIN} = process.env;

	if (!APP_ORIGIN) {
		throw new Error(`Application is not initialized. Please ensure '_createApp' has been invoked before using this method.`);
	}

	return APP_ORIGIN;
};

export const uuidv4 = (): string =>
	((1e7).toString() + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
		(Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16),
	);
